import { CheckCircle2, Circle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { ApiError } from "../../shared/api/client";
import type { MeetingDetail, MeetingPointView, WorkItemType } from "../../shared/types/api";
import { Button, Card, CardBody, CardHeader, InlineAlert, Textarea } from "../../shared/ui";
import { formatTimestamp } from "../workitems/workItemVocabulary";
import { MakeWorkItemDrawer } from "./MeetingDrawers";

/**
 * Minutes that have been submitted: read-only, except what happens after the meeting — actions
 * ticked off, points turned into work — and corrections added beneath.
 */
export function SubmittedMinutes({
  meeting,
  onTick,
  onMakeWorkItem,
  onCorrect,
}: {
  meeting: MeetingDetail;
  onTick: (point: MeetingPointView, done: boolean) => Promise<void>;
  onMakeWorkItem: (point: MeetingPointView, type: WorkItemType) => Promise<void>;
  onCorrect: (body: string) => Promise<void>;
}) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [making, setMaking] = useState<MeetingPointView | null>(null);
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayTick = (point: MeetingPointView) =>
    meeting.canRecordMinutes || (!!user && point.actionOwner?.id === user.employeeId);

  const attempt = async (action: () => Promise<void>) => {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That did not work.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Minutes"
        subtitle={`Submitted by ${meeting.minutesSubmittedBy?.fullName ?? "someone"} ${
          meeting.minutesSubmittedAt ? formatTimestamp(meeting.minutesSubmittedAt) : ""
        }. They can no longer be changed; add a correction instead.`}
      />
      <CardBody className="space-y-5">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <p className="whitespace-pre-wrap text-sm text-ink-800">{meeting.minutesSummary}</p>

        <ol className="space-y-3">
          {meeting.points.map((point) => (
            <li key={point.id} className="rounded-lg border border-ink-100 p-3">
              <p className="whitespace-pre-wrap text-sm text-ink-900">
                <span className="text-ink-400">{point.ordinal}. </span>
                {point.body}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-500">
                {point.actionOwner && (
                  <button
                    type="button"
                    disabled={!mayTick(point)}
                    className="inline-flex items-center gap-1.5 disabled:cursor-default"
                    onClick={() => void attempt(() => onTick(point, !point.actionDoneAt))}
                  >
                    {point.actionDoneAt ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-ink-300" />
                    )}
                    {point.actionOwner.fullName}
                    {point.actionDueDate && ` · due ${point.actionDueDate}`}
                    {point.actionDoneAt && ` · done`}
                  </button>
                )}
                {point.workItemId ? (
                  <button
                    type="button"
                    className="text-brand-700 hover:underline"
                    onClick={() => navigate(`/work-items/${point.workItemKey}`)}
                  >
                    {point.workItemKey}
                  </button>
                ) : (
                  can("WORK_ITEM_CREATE") && (
                    <Button variant="ghost" size="sm" onClick={() => setMaking(point)}>
                      Make work item
                    </Button>
                  )
                )}
              </div>
            </li>
          ))}
        </ol>

        {meeting.corrections.length > 0 && (
          <div className="space-y-2 border-t border-ink-100 pt-4">
            <p className="text-sm font-medium text-ink-900">Corrections</p>
            {meeting.corrections.map((c) => (
              <div key={c.id} className="rounded-lg bg-amber-50 p-3 text-sm">
                <p className="whitespace-pre-wrap text-ink-800">{c.body}</p>
                <p className="mt-1 text-xs text-ink-500">
                  {c.addedBy?.fullName ?? "Someone"} · {formatTimestamp(c.addedAt)}
                </p>
              </div>
            ))}
          </div>
        )}

        {meeting.canRecordMinutes && (
          <div className="space-y-2 border-t border-ink-100 pt-4">
            <Textarea
              aria-label="Correction"
              rows={2}
              placeholder="Something in the minutes is wrong? Say what, and it will show beneath them."
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                disabled={!correction.trim()}
                loading={saving}
                onClick={() =>
                  void (async () => {
                    setSaving(true);
                    await attempt(async () => {
                      await onCorrect(correction.trim());
                      setCorrection("");
                    });
                    setSaving(false);
                  })()
                }
              >
                Add correction
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <MakeWorkItemDrawer
        point={making}
        onClose={() => setMaking(null)}
        onMake={(type) => onMakeWorkItem(making!, type)}
      />
    </Card>
  );
}
